import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type { FreqPoint } from "../types/dashboard";
import { normalizeFopHz } from "../utils/freqHz";

export const FREQ_TIME_RANGES = ["1m", "5m", "15m", "1h", "3h"] as const;
export type FreqTimeRange = (typeof FREQ_TIME_RANGES)[number];
export const FREQ_DEFAULT_RANGE: FreqTimeRange = "1m";

/** Dashboard hero — no live 1min window. */
export const DASHBOARD_FREQ_RANGES = ["5m", "15m", "1h", "3h"] as const satisfies readonly FreqTimeRange[];

export const FREQ_RANGE_LABELS: Record<FreqTimeRange, string> = {
  "1m": "1min",
  "5m": "5m",
  "15m": "15m",
  "1h": "1h",
  "3h": "3h",
};

type FreqScope = "/" | "/frequency";

type FreqScopeState = {
  range: FreqTimeRange;
  showFop: boolean;
  showFopThreshold: boolean;
};

type FreqRangeValue = {
  range: FreqTimeRange;
  setRange: (r: FreqTimeRange) => void;
  showFop: boolean;
  setShowFop: (v: boolean) => void;
  showFopThreshold: boolean;
  setShowFopThreshold: (v: boolean) => void;
  rangePoints: FreqPoint[];
  rangeLoading: boolean;
  rangeError: string | null;
  refreshRange: () => void;
};

const FreqRangeContext = createContext<FreqRangeValue | null>(null);

const LIVE_FREQ_PATHS = new Set<FreqScope>(["/", "/frequency"]);

const SCOPE_DEFAULTS: Record<FreqScope, FreqScopeState> = {
  "/": { range: "5m", showFop: true, showFopThreshold: false },
  "/frequency": { range: "1m", showFop: false, showFopThreshold: false },
};

function scopeFromPath(pathname: string): FreqScope | null {
  if (pathname === "/") return "/";
  if (pathname === "/frequency") return "/frequency";
  return null;
}

function normalizePoint(raw: Partial<FreqPoint>): FreqPoint | null {
  const ts_ms = Number(raw.ts_ms);
  const freq_hz = Number(raw.freq_hz);
  if (!Number.isFinite(ts_ms) || !Number.isFinite(freq_hz)) return null;
  const fop_hz = normalizeFopHz(raw.fop_hz);
  return {
    ts_ms,
    freq_hz,
    fop_hz,
    raw_u16: Number.isFinite(raw.raw_u16) ? Number(raw.raw_u16) : 0,
  };
}

export function FreqRangeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const scope = scopeFromPath(pathname);
  const wantsRange = scope != null && LIVE_FREQ_PATHS.has(scope);

  const [scopeState, setScopeState] = useState<Record<FreqScope, FreqScopeState>>(SCOPE_DEFAULTS);
  const [rangePoints, setRangePoints] = useState<FreqPoint[]>([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [rangeRefreshKey, setRangeRefreshKey] = useState(0);

  const refreshRange = useCallback(() => {
    setRangeRefreshKey((k) => k + 1);
  }, []);

  const active = scope ? scopeState[scope] : SCOPE_DEFAULTS["/"];
  const activeRange = scope ? scopeState[scope].range : null;

  const patchScope = useCallback((path: FreqScope, patch: Partial<FreqScopeState>) => {
    setScopeState((prev) => ({
      ...prev,
      [path]: { ...prev[path], ...patch },
    }));
  }, []);

  const setRange = useCallback(
    (r: FreqTimeRange) => {
      if (!scope) return;
      patchScope(scope, { range: r });
    },
    [scope, patchScope],
  );

  const setShowFop = useCallback(
    (v: boolean) => {
      if (!scope) return;
      patchScope(scope, { showFop: v });
    },
    [scope, patchScope],
  );

  const setShowFopThreshold = useCallback(
    (v: boolean) => {
      if (!scope) return;
      patchScope(scope, { showFopThreshold: v });
    },
    [scope, patchScope],
  );

  useEffect(() => {
    if (!wantsRange || !scope) {
      setRangePoints([]);
      setRangeLoading(false);
      setRangeError(null);
      return;
    }

    const range = activeRange;
    if (!range) return;

    // Shortest range uses live MQTT in FreqHeroChart — no QuestDB round-trip.
    if (range === FREQ_DEFAULT_RANGE) {
      setRangePoints([]);
      setRangeLoading(false);
      setRangeError(null);
      return;
    }

    let cancelled = false;
    setRangeLoading(true);
    setRangeError(null);

    (async () => {
      try {
        const res = await fetch(`/api/freq/history/range?range=${encodeURIComponent(range)}`, {
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { points?: Partial<FreqPoint>[] };
        if (cancelled) return;
        const points = (data.points ?? [])
          .map((p) => normalizePoint(p))
          .filter((p): p is FreqPoint => p !== null);
        setRangePoints(points);
      } catch (err) {
        if (!cancelled) {
          setRangeError(err instanceof Error ? err.message : "Range fetch failed");
          setRangePoints([]);
        }
      } finally {
        if (!cancelled) setRangeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scope, wantsRange, activeRange, rangeRefreshKey]);

  const value = useMemo<FreqRangeValue>(
    () => ({
      range: active.range,
      setRange,
      showFop: active.showFop,
      setShowFop,
      showFopThreshold: active.showFopThreshold,
      setShowFopThreshold,
      rangePoints,
      rangeLoading,
      rangeError,
      refreshRange,
    }),
    [
      active.range,
      active.showFop,
      active.showFopThreshold,
      setRange,
      setShowFop,
      setShowFopThreshold,
      rangePoints,
      rangeLoading,
      rangeError,
      refreshRange,
    ],
  );

  return <FreqRangeContext.Provider value={value}>{children}</FreqRangeContext.Provider>;
}

export function useFreqRange() {
  return useContext(FreqRangeContext);
}
